import { useEffect, useState } from 'react'
import { useMatch } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { Check, Clock, ShoppingBag, X } from 'lucide-react'
import { StoreImage } from '@/store/components/StoreImage'
import {
  PURCHASE_ACTIVITY_DISPLAY_MS,
  usePurchaseActivityToast,
} from '@/store/hooks/usePurchaseActivityToast'
import { purchaseActivityEventKey } from '@/services/store/purchaseActivity'
import { cn } from '@/lib/utils'

const FOOTER_GAP_PX = 10
/** Show “just now” only for purchases younger than this. */
const JUST_NOW_MAX_MS = 3 * 60 * 1000
/** Cracker / confetti burst duration when the toast appears. */
const CRACKER_BURST_MS = 3600

const toastSpring = { type: 'spring' as const, stiffness: 420, damping: 32, mass: 0.85 }

const CRACKER_COLORS = [
  '#fff8e1',
  '#f9e076',
  '#ecd06a',
  '#d4af37',
  '#c5a059',
  '#b8943f',
  '#9a7d32',
  '#7a5c2e',
  '#34d399',
  '#fbbf24',
]

type CrackerShape = 'dot' | 'ribbon' | 'diamond' | 'spark' | 'streamer'

const CRACKER_PIECES = Array.from({ length: 48 }, (_, index) => {
  const wave = index < 28 ? 0 : 1
  const localIndex = wave === 0 ? index : index - 28
  const localCount = wave === 0 ? 28 : 20
  const angle =
    -180 +
    (localIndex / Math.max(localCount - 1, 1)) * 360 +
    ((index * 23) % 19) -
    9
  const distance =
    (wave === 0 ? 70 : 92) + (index % 7) * 18 + (index % 4) * 8
  const shape: CrackerShape =
    index % 11 === 0
      ? 'streamer'
      : index % 7 === 0
        ? 'spark'
        : index % 5 === 0
          ? 'diamond'
          : index % 3 === 0
            ? 'ribbon'
            : 'dot'
  const size =
    shape === 'streamer'
      ? 14
      : shape === 'spark'
        ? 9
        : shape === 'diamond'
          ? 7
          : shape === 'ribbon'
            ? 10
            : index % 4 === 0
              ? 5.5
              : 3.5

  return {
    id: index,
    angle,
    distance,
    size,
    shape,
    color: CRACKER_COLORS[index % CRACKER_COLORS.length],
    delay: wave * 0.14 + (localIndex % 8) * 0.022,
    spin: (index % 2 === 0 ? 1 : -1) * (260 + (index % 7) * 95),
    drift: ((index % 7) - 3) * 10,
  }
})

function isJustNow(purchasedAt: string): boolean {
  const purchasedMs = new Date(purchasedAt).getTime()
  if (!Number.isFinite(purchasedMs)) return false
  return Date.now() - purchasedMs < JUST_NOW_MAX_MS
}

function PurchaseCrackerBurst({ burstKey }: { burstKey: string }) {
  const reduceMotion = useReducedMotion()
  const [active, setActive] = useState(true)

  useEffect(() => {
    setActive(true)
    if (reduceMotion) return

    const timer = window.setTimeout(() => setActive(false), CRACKER_BURST_MS)
    return () => window.clearTimeout(timer)
  }, [burstKey, reduceMotion])

  if (reduceMotion || !active) return null

  return (
    <div className="purchase-activity-toast__cracker" aria-hidden>
      {CRACKER_PIECES.map((piece) => {
        const rad = (piece.angle * Math.PI) / 180
        const lift = piece.shape === 'streamer' ? 42 : 22
        const x = Math.cos(rad) * piece.distance + piece.drift
        const y = Math.sin(rad) * piece.distance - lift
        const fallY = y + 68 + (piece.id % 6) * 14
        const isStreamer = piece.shape === 'streamer'

        return (
          <motion.span
            key={`${burstKey}-${piece.id}`}
            className={cn(
              'purchase-activity-toast__cracker-piece',
              `purchase-activity-toast__cracker-piece--${piece.shape}`
            )}
            style={{
              width: isStreamer ? piece.size * 0.28 : piece.size,
              height: isStreamer
                ? piece.size
                : piece.shape === 'ribbon'
                  ? piece.size * 0.36
                  : piece.size,
              background: piece.color,
              color: piece.color,
            }}
            initial={{ opacity: 0, x: 0, y: 0, scale: 0, rotate: 0 }}
            animate={{
              opacity: [0, 1, 1, 0.7, 0],
              x: [0, x * 0.28, x * 0.72, x * 1.08, x * 1.22],
              y: [0, y * 0.28 - 8, y * 0.7, fallY * 0.78, fallY],
              scale: isStreamer
                ? [0, 1.2, 1.05, 0.85, 0.3]
                : piece.shape === 'spark'
                  ? [0, 1.7, 1.15, 0.65, 0.15]
                  : [0, 1.45, 1.1, 0.7, 0.2],
              rotate: [
                0,
                piece.spin * 0.2,
                piece.spin * 0.5,
                piece.spin * 0.85,
                piece.spin * 1.15,
              ],
            }}
            transition={{
              duration: CRACKER_BURST_MS / 1000,
              ease: [0.12, 0.8, 0.2, 1],
              delay: piece.delay,
              times: [0, 0.08, 0.28, 0.62, 1],
            }}
          />
        )
      })}
    </div>
  )
}

interface PurchaseActivityToastProps {
  aboveBottomNav?: boolean
}

export function PurchaseActivityToast({
  aboveBottomNav = false,
}: PurchaseActivityToastProps) {
  const isProductDetail = Boolean(useMatch('/shop/:id'))
  const { visible, activity, dismiss } = usePurchaseActivityToast()
  const [liftPx, setLiftPx] = useState(0)

  useEffect(() => {
    const updatePosition = () => {
      const doc = document.documentElement
      const distanceFromBottom =
        doc.scrollHeight - doc.scrollTop - window.innerHeight
      const atPageBottom = distanceFromBottom <= 2

      if (!atPageBottom) {
        setLiftPx(0)
        return
      }

      const footerCredit = document.getElementById('store-footer-credit')
      setLiftPx(
        footerCredit
          ? footerCredit.offsetHeight + FOOTER_GAP_PX
          : FOOTER_GAP_PX
      )
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, { passive: true })
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
    }
  }, [])

  const useBottomNavOffset = aboveBottomNav && !isProductDetail
  const activityKey = activity ? purchaseActivityEventKey(activity) : ''
  const showJustNow = activity ? isJustNow(activity.purchasedAt) : false

  return (
    <AnimatePresence mode="wait">
      {visible && activity ? (
        <motion.aside
          key={activityKey}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          initial={{ opacity: 0, x: -28, y: 12, scale: 0.94 }}
          animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
          exit={{ opacity: 0, x: -20, y: 8, scale: 0.97 }}
          transition={toastSpring}
          style={{
            transform:
              liftPx > 0 ? `translateY(-${liftPx}px)` : undefined,
          }}
          className={cn(
            'purchase-activity-toast fixed left-4 z-50 max-w-[min(18.5rem,calc(100vw-2rem))] overflow-visible',
            isProductDetail
              ? 'product-mobile-bar-offset-bottom lg:bottom-6'
              : useBottomNavOffset
                ? 'store-bottom-nav-offset lg:bottom-6'
                : 'bottom-6'
          )}
        >
          {showJustNow ? <PurchaseCrackerBurst burstKey={activityKey} /> : null}

          <div className="purchase-activity-toast__frame relative rounded-2xl p-px shadow-[0_20px_50px_-12px_rgba(122,92,46,0.35)]">
            <div className="relative overflow-hidden rounded-[calc(1rem-1px)] bg-white/92 backdrop-blur-xl">
              <div
                className="pointer-events-none absolute inset-0 bg-gradient-to-br from-gold-100/50 via-white/20 to-transparent"
                aria-hidden
              />

              <motion.div
                key={`progress-${activityKey}`}
                className="purchase-activity-toast__progress absolute inset-x-0 top-0 h-[2px] origin-left"
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0 }}
                transition={{
                  duration: PURCHASE_ACTIVITY_DISPLAY_MS / 1000,
                  ease: 'linear',
                }}
                aria-hidden
              />

              <button
                type="button"
                onClick={dismiss}
                className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-red-500/75 text-white shadow-sm backdrop-blur-sm transition-all hover:bg-red-500/90 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400/50"
                aria-label="Dismiss notification"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>

              <div className="relative flex items-center gap-3.5 p-3.5 pr-11">
                <div className="relative shrink-0">
                  <div
                    className="absolute -inset-1 rounded-[0.85rem] bg-gradient-to-br from-gold-300/50 via-gold-500/20 to-gold-700/30 blur-[2px]"
                    aria-hidden
                  />
                  <div className="relative h-[3.25rem] w-[3.25rem] overflow-hidden rounded-xl bg-luxury-900 ring-1 ring-gold-500/25">
                    {activity.productImage ? (
                      <StoreImage
                        src={activity.productImage}
                        alt={activity.productName}
                        className="h-full w-full"
                        containerClassName="h-full w-full"
                        objectFit="cover"
                        spinnerSize="sm"
                        fallback={
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gold-50 to-gold-100/80 text-gold-700">
                            <ShoppingBag className="h-5 w-5" aria-hidden />
                          </div>
                        }
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-gold-50 to-gold-100/80 text-gold-700">
                        <ShoppingBag className="h-5 w-5" aria-hidden />
                      </div>
                    )}
                  </div>
                  <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 shadow-md shadow-emerald-900/20 ring-2 ring-white">
                    <Check className="h-3 w-3 stroke-[2.5] text-white" aria-hidden />
                  </span>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-1.5">
                    <span className="purchase-activity-toast__pulse h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                    <p className="whitespace-nowrap text-[11px] font-medium tracking-wide text-zinc-500">
                      Someone recently purchased
                    </p>
                  </div>

                  <p className="font-display text-[15px] font-semibold leading-snug text-zinc-900 line-clamp-2">
                    {activity.productName}
                  </p>

                  {(activity.variant || showJustNow) && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {activity.variant ? (
                        <span className="inline-flex rounded-full bg-gold-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gold-800 ring-1 ring-gold-500/20">
                          {activity.variant}
                        </span>
                      ) : null}
                      {showJustNow ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
                          <Clock className="h-3 w-3 shrink-0 text-gold-600/80" aria-hidden />
                          just now
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.aside>
      ) : null}
    </AnimatePresence>
  )
}
